// Polish Phase 2 (PARTICLE-01/02/04/06/07/08/09, PERF-03/09, THEME-01) —
// the real pooled GPU particle layer.
//
// Before this, EffectLayer was an empty container and ParticleEmitter.emit()
// returned bare {id, kind} objects that nothing rendered. This is the actual
// simulation + renderer: a fixed pool of Pixi Sprites (all sharing ONE soft-dot
// texture so they batch into ~1 draw call regardless of count — the §16.4 draw
// budget), a per-kind spec table covering all 12 canon §14 effects, a
// ticker-driven Euler simulation, and a theme palette so particles tint to the
// active theme at emit time.
//
// PART-05 — RENDER COVERAGE: all 12 kinds are spec'd, pooled, and tinted, but
// only SIX have production emit call-sites today: the three cube-lifecycle
// bursts (rune-carve / stone-carve / stone-fall, self-fired by LetterCubeSprite)
// plus coin-spark (token claim), magic-bloom (empower / Quill target), and
// dark-pulse (king-capture + opponent-empower threat), emitted via the
// $particleBurst DOM->Pixi bridge (INT-01). The remaining six (dust-puff,
// player-a/b shimmer, ribbon-burst, mote, mythic-sparkle) are spec'd/tinted but
// currently fire only in the dev perf harness — they get wired as their canon
// moments land.
//
// POOLING (PERF-08/09): MAX_PARTICLES sprites are created ONCE in the
// constructor and recycled via a free-list. emit() never allocates a sprite;
// when the pool is exhausted it recycles the oldest active particle. update()
// allocates nothing. dispose() destroys the pool.
//
// REDUCED MOTION (PARTICLE-09 / A11Y-PART-01): emit() returns 0 and spawns
// nothing when reduced=true — the gate lives at the render/seed layer, so no
// caller can accidentally bypass it.

import { Container, Sprite, Texture } from 'pixi.js'
import type { ThemeDescriptor } from '../theme/descriptor'
import { extractRuneThemeTokens } from '../theme/extract-rune-tokens'
import type { ParticleKind } from './particles'

/** Palette role names. Universal roles are theme-CONSTANT (action/text tokens);
 * the rest resolve from the active theme descriptor. */
export type PaletteRole =
  | 'accent' // Sun Yellow (universal)
  | 'cream' // Bone (universal)
  | 'inkwell' // Inkwell (universal)
  | 'brass' // Forged ring brass (universal default)
  | 'gold' // Mythic gold (universal default)
  | 'silver' // Rare silver (universal default)
  | 'warmGrey' // stone crumble (universal)
  | 'ember' // warm ember (universal)
  | 'magic' // theme magic
  | 'magicDeep' // theme magic deep
  | 'playerA' // theme player A
  | 'playerB' // theme player B

export type ParticlePalette = Readonly<Record<PaletteRole, number>>

/** Universal + Court-of-Runes default palette. The theme-varying roles
 * (magic/player) are overwritten by EffectLayer from the active descriptor;
 * the universal roles never change (they mirror :root action/text tokens).
 * 0x-prefixed ints are NOT off-token literals (the gate matches #-hex only),
 * and these universal values are theme-constant by canon (§3.1). */
export const DEFAULT_PALETTE: ParticlePalette = {
  // Folk reskin: warm fallbacks (the live palette resolves from the folk
  // descriptor; these mirror the folk token values for safety).
  accent: 0xf7a920, // gold
  cream: 0xfff8ec,
  inkwell: 0x3b2a1c,
  brass: 0xc9981f,
  gold: 0xffd96b,
  silver: 0xc8d4e0,
  warmGrey: 0x9a8f86,
  ember: 0xff7152,
  magic: 0x7b6bdd,
  magicDeep: 0x5847c4,
  playerA: 0xee5d43,
  playerB: 0x2ba39c,
}

/** [r,g,b] float triple (pixi_uniforms format) → 0xRRGGBB int. */
function uniformToInt(triple: readonly [number, number, number]): number {
  const r = Math.max(0, Math.min(255, Math.round(triple[0] * 255)))
  const g = Math.max(0, Math.min(255, Math.round(triple[1] * 255)))
  const b = Math.max(0, Math.min(255, Math.round(triple[2] * 255)))
  return (r << 16) | (g << 8) | b
}

/**
 * Build a theme-tinted particle palette from a validated descriptor: the
 * theme-varying roles (magic / player) come from pixi_uniforms, the
 * theme-tunable metals (brass/gold/silver) from the --rune-* tier tokens
 * (so Emerald's copper forged ring tints its stone-carve sparks, etc.), and
 * the universal roles stay constant. Degrades to DEFAULT_PALETTE on any
 * malformed descriptor — particles must never crash the scene (THEME-01).
 */
export function paletteFromDescriptor(
  descriptor: ThemeDescriptor,
): ParticlePalette {
  try {
    const u = descriptor.pixi_uniforms
    const rune = extractRuneThemeTokens(descriptor)
    const pick = (key: string): number | null => {
      const t = u[key]
      return Array.isArray(t) && t.length === 3
        ? uniformToInt(t as [number, number, number])
        : null
    }
    return {
      ...DEFAULT_PALETTE,
      magic: pick('color_magic') ?? DEFAULT_PALETTE.magic,
      magicDeep: pick('color_magic_deep') ?? DEFAULT_PALETTE.magicDeep,
      playerA: pick('color_player_a') ?? DEFAULT_PALETTE.playerA,
      playerB: pick('color_player_b') ?? DEFAULT_PALETTE.playerB,
      brass: rune.tierForgedRingColor,
      gold: rune.tierMythicGlowColor,
      silver: rune.tierRareInlayColor,
    }
  } catch {
    return DEFAULT_PALETTE
  }
}

type Direction = 'radial' | 'up' | 'down' | 'drift'

interface KindSpec {
  /** Default particle count for one burst (callers may override). */
  readonly count: number
  /** Lifetime per particle in ms. */
  readonly lifeMs: number
  /** Initial speed magnitude px/s. */
  readonly speed: number
  /** ± randomization fraction of speed. */
  readonly speedVar: number
  /** Gravity px/s² (positive = downward). */
  readonly gravity: number
  /** Emission direction. */
  readonly dir: Direction
  /** Angular spread in degrees around the direction. */
  readonly spreadDeg: number
  /** Start size px [min, max]. */
  readonly size: readonly [number, number]
  /** Size multiplier at end of life (>1 expand, <1 shrink). */
  readonly sizeEndMul: number
  /** Fraction of life spent fading IN (0 = instant on). */
  readonly fadeIn: number
  /** Max spin rad/s (± random). */
  readonly spin: number
  /** Peak alpha. */
  readonly alpha: number
  /** Palette roles to pick a tint from. */
  readonly roles: readonly PaletteRole[]
  /** Additive blend (sparkle/glow) vs normal. */
  readonly additive: boolean
}

/** Per-kind specs, faithful to VisualDesign §14 (count / lifetime / motion). */
const SPECS: Readonly<Record<ParticleKind, KindSpec>> = {
  'dust-puff': {
    count: 6,
    lifeMs: 200,
    speed: 70,
    speedVar: 0.5,
    gravity: 40,
    dir: 'radial',
    spreadDeg: 360,
    size: [4, 7],
    sizeEndMul: 0.4,
    fadeIn: 0.05,
    spin: 0,
    alpha: 0.6,
    roles: ['cream', 'warmGrey'],
    additive: false,
  },
  'coin-spark': {
    count: 7,
    lifeMs: 400,
    speed: 150,
    speedVar: 0.6,
    gravity: 420,
    dir: 'up',
    spreadDeg: 120,
    size: [3, 6],
    sizeEndMul: 0.6,
    fadeIn: 0.05,
    spin: 0,
    alpha: 0.95,
    roles: ['accent', 'cream', 'gold'],
    additive: true,
  },
  'magic-bloom': {
    count: 12,
    lifeMs: 600,
    speed: 90,
    speedVar: 0.5,
    gravity: -20,
    dir: 'radial',
    spreadDeg: 360,
    size: [4, 9],
    sizeEndMul: 1.8,
    fadeIn: 0.12,
    spin: 1.5,
    alpha: 0.9,
    roles: ['magic', 'cream', 'magicDeep'],
    additive: true,
  },
  'player-a-shimmer': {
    count: 8,
    lifeMs: 500,
    speed: 80,
    speedVar: 0.6,
    gravity: -10,
    dir: 'radial',
    spreadDeg: 360,
    size: [3, 7],
    sizeEndMul: 1.2,
    fadeIn: 0.1,
    spin: 0.8,
    alpha: 0.85,
    roles: ['playerA', 'cream'],
    additive: true,
  },
  'player-b-ember': {
    count: 6,
    lifeMs: 500,
    speed: 70,
    speedVar: 0.5,
    gravity: -90,
    dir: 'up',
    spreadDeg: 70,
    size: [3, 6],
    sizeEndMul: 0.7,
    fadeIn: 0.1,
    spin: 0,
    alpha: 0.85,
    roles: ['playerB', 'ember'],
    additive: true,
  },
  'dark-pulse': {
    count: 1,
    lifeMs: 500,
    speed: 0,
    speedVar: 0,
    gravity: 0,
    dir: 'radial',
    spreadDeg: 0,
    size: [30, 30],
    sizeEndMul: 7,
    fadeIn: 0.06,
    spin: 0,
    alpha: 0.5,
    roles: ['inkwell'],
    additive: false,
  },
  mote: {
    count: 1,
    lifeMs: 4000,
    speed: 14,
    speedVar: 0.8,
    gravity: -4,
    dir: 'drift',
    spreadDeg: 360,
    size: [3, 6],
    sizeEndMul: 1,
    fadeIn: 0.2,
    spin: 0.2,
    alpha: 0.4,
    roles: ['accent', 'magic', 'cream'],
    additive: true,
  },
  'ribbon-burst': {
    count: 6,
    lifeMs: 800,
    speed: 140,
    speedVar: 0.5,
    gravity: 120,
    dir: 'radial',
    spreadDeg: 360,
    size: [5, 9],
    sizeEndMul: 0.8,
    fadeIn: 0.05,
    spin: 3,
    alpha: 0.9,
    roles: ['accent', 'magic', 'playerA', 'gold'],
    additive: false,
  },
  'rune-carve': {
    count: 6,
    lifeMs: 250,
    speed: 60,
    speedVar: 0.6,
    gravity: -30,
    dir: 'up',
    spreadDeg: 120,
    size: [3, 6],
    sizeEndMul: 0.7,
    fadeIn: 0.08,
    spin: 0,
    alpha: 0.85,
    roles: ['cream', 'accent'],
    additive: true,
  },
  'mythic-sparkle': {
    count: 1,
    lifeMs: 700,
    speed: 18,
    speedVar: 0.8,
    gravity: 0,
    dir: 'radial',
    spreadDeg: 360,
    size: [3, 6],
    sizeEndMul: 0.3,
    fadeIn: 0.15,
    spin: 2,
    alpha: 0.95,
    roles: ['gold', 'cream'],
    additive: true,
  },
  'stone-fall': {
    count: 8,
    lifeMs: 350,
    speed: 50,
    speedVar: 0.7,
    gravity: 520,
    dir: 'down',
    spreadDeg: 80,
    size: [3, 7],
    sizeEndMul: 0.6,
    fadeIn: 0.03,
    spin: 4,
    alpha: 0.85,
    roles: ['warmGrey', 'inkwell'],
    additive: false,
  },
  'stone-carve': {
    count: 10,
    lifeMs: 500,
    speed: 80,
    speedVar: 0.6,
    gravity: -40,
    dir: 'up',
    spreadDeg: 200,
    size: [3, 7],
    sizeEndMul: 0.8,
    fadeIn: 0.08,
    spin: 1,
    alpha: 0.9,
    roles: ['brass', 'accent', 'cream'],
    additive: true,
  },
}

const TEXTURE_SIZE = 32
const MAX_PARTICLES = 512

interface ParticleRecord {
  active: boolean
  age: number
  life: number
  x: number
  y: number
  vx: number
  vy: number
  gravity: number
  size: number
  sizeEnd: number
  rot: number
  spin: number
  alpha: number
  fadeIn: number
  tint: number
  additive: boolean
}

/** Build a soft radial-gradient dot texture once. Falls back to a flat white
 * texture when no 2D canvas is available (e.g. happy-dom unit tests). */
function makeDotTexture(): Texture {
  if (typeof document === 'undefined') return Texture.WHITE
  try {
    const canvas = document.createElement('canvas')
    canvas.width = TEXTURE_SIZE
    canvas.height = TEXTURE_SIZE
    const ctx = canvas.getContext('2d')
    if (!ctx) return Texture.WHITE
    const r = TEXTURE_SIZE / 2
    const grad = ctx.createRadialGradient(r, r, 0, r, r, r)
    grad.addColorStop(0, 'rgba(255,255,255,1)')
    grad.addColorStop(0.5, 'rgba(255,255,255,0.85)')
    grad.addColorStop(1, 'rgba(255,255,255,0)')
    ctx.fillStyle = grad
    ctx.beginPath()
    ctx.arc(r, r, r, 0, Math.PI * 2)
    ctx.fill()
    return Texture.from(canvas)
  } catch {
    return Texture.WHITE
  }
}

export interface EmitParams {
  readonly count?: number
  readonly reduced: boolean
  readonly palette?: ParticlePalette
}

/**
 * Pooled GPU particle system. One instance per match, shared by LetterLayer
 * (cube lifecycle bursts) and the cinematic/HUD layers. EffectLayer mounts
 * `view` into the scene and drives `update()` from the Pixi ticker.
 */
export class ParticleSystem {
  readonly view: Container
  private readonly texture: Texture
  // True when this system minted its own dot texture (vs the shared Texture.WHITE
  // test fallback) — only an owned texture is destroyed in dispose() (PART-02).
  private readonly ownsTexture: boolean
  private readonly sprites: Sprite[] = []
  private readonly records: ParticleRecord[] = []
  private readonly free: number[] = []
  private cursor = 0 // round-robin recycle pointer when pool exhausted
  private palette: ParticlePalette = DEFAULT_PALETTE
  private disposed = false

  constructor(max: number = MAX_PARTICLES) {
    this.view = new Container()
    this.view.label = 'ParticleSystem'
    // Particles never receive input — keep them out of hit-testing.
    this.view.eventMode = 'none'
    this.texture = makeDotTexture()
    this.ownsTexture = this.texture !== Texture.WHITE
    for (let i = 0; i < max; i++) {
      const sprite = new Sprite(this.texture)
      sprite.anchor.set(0.5)
      sprite.visible = false
      this.view.addChild(sprite)
      this.sprites.push(sprite)
      this.records.push({
        active: false,
        age: 0,
        life: 0,
        x: 0,
        y: 0,
        vx: 0,
        vy: 0,
        gravity: 0,
        size: 0,
        sizeEnd: 0,
        rot: 0,
        spin: 0,
        alpha: 0,
        fadeIn: 0,
        tint: 0xffffff,
        additive: false,
      })
      this.free.push(i)
    }
  }

  /** Replace the theme-varying palette roles (magic/player tints). */
  setPalette(palette: ParticlePalette): void {
    this.palette = palette
  }

  /** True once dispose() has run — lets the provider recreate a system that a
   * StrictMode mount/unmount/remount cycle tore down (PERF-LEAK-01). */
  get isDisposed(): boolean {
    return this.disposed
  }

  /** Number of live particles (test/perf inspection). */
  get activeCount(): number {
    let n = 0
    for (const r of this.records) if (r.active) n++
    return n
  }

  private acquire(): number {
    const idx = this.free.pop()
    if (idx !== undefined) return idx
    // Pool exhausted — recycle round-robin (oldest-ish). No allocation.
    const recycled = this.cursor
    this.cursor = (this.cursor + 1) % this.records.length
    return recycled
  }

  /**
   * Spawn a burst of `kind` centred at (x, y). Returns the number spawned.
   * Reduced motion → 0 (no particles, no sprite churn).
   */
  emit(kind: ParticleKind, x: number, y: number, params: EmitParams): number {
    if (this.disposed) return 0
    if (params.reduced) return 0
    const spec = SPECS[kind]
    if (!spec) return 0
    const palette = params.palette ?? this.palette
    const count = params.count ?? spec.count
    const dirBase =
      spec.dir === 'up' ? -Math.PI / 2 : spec.dir === 'down' ? Math.PI / 2 : 0 // radial/drift start angle is randomized below
    const spread = (spec.spreadDeg * Math.PI) / 180

    let spawned = 0
    for (let i = 0; i < count; i++) {
      const idx = this.acquire()
      const rec = this.records[idx]
      const sprite = this.sprites[idx]
      if (!rec || !sprite) continue

      const angle =
        spec.dir === 'radial' || spec.dir === 'drift'
          ? Math.random() * Math.PI * 2
          : dirBase + (Math.random() - 0.5) * spread
      const speed = spec.speed * (1 + (Math.random() - 0.5) * 2 * spec.speedVar)
      const size = spec.size[0] + Math.random() * (spec.size[1] - spec.size[0])
      const roleList = spec.roles
      const tint =
        palette[
          roleList[Math.floor(Math.random() * roleList.length)] ?? 'cream'
        ]

      rec.active = true
      rec.age = 0
      rec.life = spec.lifeMs
      rec.x = x
      rec.y = y
      rec.vx = Math.cos(angle) * speed
      rec.vy = Math.sin(angle) * speed
      rec.gravity = spec.gravity
      rec.size = size
      rec.sizeEnd = size * spec.sizeEndMul
      rec.rot = Math.random() * Math.PI * 2
      rec.spin = (Math.random() - 0.5) * 2 * spec.spin
      rec.alpha = spec.alpha
      rec.fadeIn = spec.fadeIn
      rec.tint = tint
      rec.additive = spec.additive

      sprite.visible = true
      sprite.tint = tint
      sprite.blendMode = spec.additive ? 'add' : 'normal'
      sprite.x = x
      sprite.y = y
      sprite.rotation = rec.rot
      sprite.alpha = spec.fadeIn > 0 ? 0 : spec.alpha
      sprite.scale.set(size / TEXTURE_SIZE)
      spawned++
    }
    return spawned
  }

  /** Advance the simulation by `dtMs` and sync sprite transforms. */
  update(dtMs: number): void {
    if (this.disposed || dtMs <= 0) return
    const dt = dtMs / 1000
    for (let i = 0; i < this.records.length; i++) {
      const rec = this.records[i]
      if (!rec?.active) continue
      rec.age += dtMs
      const sprite = this.sprites[i]
      if (rec.age >= rec.life) {
        rec.active = false
        if (sprite) sprite.visible = false
        this.free.push(i)
        continue
      }
      const t = rec.age / rec.life
      rec.vy += rec.gravity * dt
      rec.x += rec.vx * dt
      rec.y += rec.vy * dt
      rec.rot += rec.spin * dt
      // Fade in then ease out.
      let a: number
      if (rec.fadeIn > 0 && t < rec.fadeIn) {
        a = t / rec.fadeIn
      } else {
        const tt = rec.fadeIn > 0 ? (t - rec.fadeIn) / (1 - rec.fadeIn) : t
        a = 1 - tt * tt // ease-out quad
      }
      const px = rec.size + (rec.sizeEnd - rec.size) * t
      if (sprite) {
        sprite.x = rec.x
        sprite.y = rec.y
        sprite.rotation = rec.rot
        sprite.alpha = Math.max(0, a) * rec.alpha
        sprite.scale.set(px / TEXTURE_SIZE)
      }
    }
  }

  /** Deactivate all live particles (e.g. on scene reset) without freeing the
   * pool. */
  clear(): void {
    for (let i = 0; i < this.records.length; i++) {
      const rec = this.records[i]
      const sprite = this.sprites[i]
      if (rec?.active) {
        rec.active = false
        if (sprite) sprite.visible = false
        this.free.push(i)
      }
    }
  }

  /** Destroy the pool + container (match unmount / app teardown). */
  dispose(): void {
    if (this.disposed) return
    this.disposed = true
    // Detach from any parent first (EffectLayer borrows the view), then
    // destroy — guarded so a double-teardown (parent already destroyed the
    // subtree) never throws.
    if (!this.view.destroyed) {
      this.view.parent?.removeChild(this.view)
      this.view.destroy({ children: true })
    }
    // Pixi v8 Container.destroy defaults texture:false/textureSource:false, so
    // the shared soft-dot Texture + its source canvas survive sprite teardown.
    // Release it (PART-02) — but never the canonical Texture.WHITE test fallback.
    if (this.ownsTexture && !this.texture.destroyed) {
      this.texture.destroy(true)
    }
    this.sprites.length = 0
    this.records.length = 0
    this.free.length = 0
  }
}
